import { useState, useEffect, useRef, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Check } from 'lucide-react'
import { service } from '../lib/api/service'
import type { Project } from '../lib/api/types'
import { useProjectConfig } from '../lib/appContext'
import { statusInfo } from '../utils/statusUtils'

const ORSAKER = [
  'Beställarens önskemål',
  'Ändring av handling',
  'Hinder',
  'Myndighetsbeslut',
]

interface NewATADialogProps {
  onClose: () => void
}

export default function NewATADialog({ onClose }: NewATADialogProps) {
  const navigate = useNavigate()
  const rubrikRef = useRef<HTMLInputElement>(null)
  const { statuses, defaultStatus } = useProjectConfig()
  const defaultLabel = statusInfo(defaultStatus, statuses).label

  const [project, setProject]         = useState<Project | null>(null)
  const [nextNr, setNextNr]           = useState<number | null>(null)
  const [rubrik, setRubrik]           = useState('')
  const [orsak, setOrsak]             = useState('')
  const [ansvarig, setAnsvarig]       = useState('')
  const [beskrivning, setBeskrivning] = useState('')
  const [paverkanTid, setPaverkanTid] = useState('')
  const [paverkanEk, setPaverkanEk]   = useState('')
  const [skapaU, setSkapaU]           = useState(true)
  const [skapaSpec, setSkapaSpec]     = useState(false)
  const [saving, setSaving]           = useState(false)
  const [error, setError]             = useState<string | null>(null)

  useEffect(() => {
    Promise.all([service.getProject(), service.getNextSortOrder()])
      .then(([p, nr]) => { setProject(p); setNextNr(nr) })
    setTimeout(() => rubrikRef.current?.focus(), 200)
  }, [])

  const today = new Date().toLocaleDateString('sv-SE')
  const nrPad = nextNr ? `ÄTA-${String(nextNr).padStart(3,'0')}` : '…'

  const reset = () => {
    setRubrik(''); setOrsak(''); setAnsvarig(''); setBeskrivning('')
    setPaverkanTid(''); setPaverkanEk(''); setSkapaU(true); setSkapaSpec(false)
    setError(null)
    service.getNextSortOrder().then(nr => setNextNr(nr))
    setTimeout(() => rubrikRef.current?.focus(), 200)
  }

  const submit = async (andNew = false) => {
    if (!rubrik.trim()) return
    setSaving(true)
    setError(null)
    try {
      const sortOrder = await service.createATA({
        rubrik: rubrik.trim(),
        beskrivning, orsak, ansvarig,
        paverkanTid:     paverkanTid || 'Bedöms',
        paverkanEkonomi: paverkanEk  || 'Bedöms',
        skapaUnderrattelse: skapaU,
        skapaSpec,
        defaultStatus,
      })
      if (andNew) {
        reset()
      } else {
        onClose()
        navigate(`/ata/${sortOrder}`)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const projectMeta = project
    ? [project.projektNamn, project.ort].filter(Boolean).join(' · ')
    : ''

  return (
    <div className="modal-scrim" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="modal-header">
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <h2 style={{ fontSize:17, fontWeight:600 }}>Ny ÄTA</h2>
              <span style={{
                fontFamily:'var(--mono)', fontSize:11.5, fontWeight:600,
                background:'var(--bg-2)', border:'.5px solid var(--border)',
                borderRadius:999, padding:'2px 9px',
              }}>
                {nrPad}
              </span>
            </div>
            <div style={{ fontSize:11, color:'var(--ink-faint)', marginTop:3 }}>
              {projectMeta && `${projectMeta} · `}Skapad {today}
            </div>
          </div>
          <button className="icon-button" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {error && (
            <div className="notification notification-error">
              <span>{error}</span>
            </div>
          )}

          {/* Block 01 */}
          <div className="block">
            <div className="block-header">
              <span className="block-nr">01</span>
              <div>
                <div className="block-title">Vad gäller ärendet?</div>
                <div className="block-hint">
                  Rubriken visas i listor och brev. Tänk dig att Anders ringer om ett halvår — vad räcker för att han ska komma ihåg?
                </div>
              </div>
            </div>

            <div className="field" style={{ marginBottom:14 }}>
              <label className="field-label">Rubrik <span style={{ color:'var(--accent)' }}>*</span></label>
              <input
                ref={rubrikRef}
                className="field-input"
                value={rubrik}
                onChange={e => setRubrik(e.target.value)}
                placeholder="t.ex. Tilläggsbeställning trefacksfönster plan 2"
              />
            </div>

            <div className="grid-2">
              <div className="field">
                <label className="field-label">Orsak</label>
                <div className="orsak-grid">
                  {ORSAKER.map(o => (
                    <button
                      key={o}
                      type="button"
                      className={`orsak-chip${orsak === o ? ' selected' : ''}`}
                      onClick={() => setOrsak(o === orsak ? '' : o)}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </div>
              <div className="field">
                <label className="field-label">Ansvarig</label>
                <input
                  className="field-input"
                  value={ansvarig}
                  onChange={e => setAnsvarig(e.target.value)}
                  placeholder="Initialer, t.ex. JB"
                />
              </div>
            </div>
          </div>

          {/* Block 02 */}
          <div className="block">
            <div className="block-header">
              <span className="block-nr">02</span>
              <div>
                <div className="block-title">Beskriv arbetet</div>
                <div className="block-hint">
                  Den här texten används som underlag för underrättelsen till beställaren.{' '}
                  <strong>Skriv som att beställaren ska läsa det</strong> — vad ska göras, varför, och var.
                </div>
              </div>
            </div>

            <div className="field" style={{ marginBottom:14 }}>
              <label className="field-label">Beskrivning</label>
              <textarea
                className="field-input"
                rows={5}
                value={beskrivning}
                onChange={e => setBeskrivning(e.target.value)}
                placeholder={'Beskriv vad som ska utföras,\nvarför det tillkommit,\noch eventuell placering i byggnaden.'}
              />
            </div>

            <div className="grid-2">
              <div className="field">
                <label className="field-label">Påverkan tid</label>
                <input
                  className="field-input"
                  value={paverkanTid}
                  onChange={e => setPaverkanTid(e.target.value)}
                  placeholder="Bedöms"
                />
                <span className="field-hint">T.ex. "Ca 5 dagar", "Ej känt", "Ingen"</span>
              </div>
              <div className="field">
                <label className="field-label">Påverkan ekonomi</label>
                <input
                  className="field-input"
                  value={paverkanEk}
                  onChange={e => setPaverkanEk(e.target.value)}
                  placeholder="Bedöms"
                />
                <span className="field-hint">T.ex. "Ca 50 000 kr", "Prissätts separat"</span>
              </div>
            </div>
          </div>

          {/* Block 03 */}
          <div className="block">
            <div className="block-header">
              <span className="block-nr">03</span>
              <div>
                <div className="block-title">Vad ska skapas samtidigt?</div>
                <div className="block-hint">
                  En ÄTA består av tre delar. Du kan slå på dem nu eller lägga till dem senare.
                </div>
              </div>
            </div>

            <StepToggle
              locked
              active
              title="Journalpost"
              status="Skapas alltid"
              body="Den interna noteringen med rubrik, beskrivning och fakturahänvisning."
              required
            />

            <StepToggle
              active={skapaU}
              onToggle={() => setSkapaU(v => !v)}
              title="Underrättelse till beställare"
              status={skapaU ? 'Skapas · status → Anmäld' : `Skapas inte · status → ${defaultLabel}`}
              body="Brev som skickas till beställaren med ovanstående beskrivning. ÄTAn får status 'Anmäld till beställare'."
            />

            <StepToggle
              active={skapaSpec}
              onToggle={() => setSkapaSpec(v => !v)}
              title="ÄTA-specifikation"
              status={skapaSpec ? 'Skapas' : 'Skapas inte'}
              body="Prissättning med rader från à-prislistan + påslag. Behövs inte direkt — kan vänta tills omfattning är känd."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="icon-btn" onClick={onClose}>Avbryt</button>
          <div style={{ flex:1 }} />
          <button
            className="icon-btn"
            onClick={() => submit(true)}
            disabled={!rubrik.trim() || saving}
          >
            Skapa &amp; ny
          </button>
          <button
            className="btn-primary"
            onClick={() => submit(false)}
            disabled={!rubrik.trim() || saving}
          >
            {saving ? 'Skapar…' : 'Skapa ÄTA →'}
          </button>
        </div>
      </div>
    </div>
  )
}

interface StepToggleProps {
  locked?: boolean
  active?: boolean
  onToggle?: () => void
  title: string
  status: string
  body: ReactNode
  required?: boolean
}

function StepToggle({ locked, active, onToggle, title, status, body, required }: StepToggleProps) {
  return (
    <div
      className={`step-toggle${active ? ' active' : ''}${locked ? ' step-toggle-locked' : ''}`}
      onClick={locked ? undefined : onToggle}
      style={{ cursor: locked ? 'default' : 'pointer' }}
    >
      <div className="step-toggle-checkbox">
        {active && <Check size={12} color="#fff" strokeWidth={3} />}
      </div>
      <div className="step-toggle-content">
        <div className="step-toggle-title">
          {title}
          <span className="step-toggle-status">{status}</span>
          {required && <span className="step-toggle-required">Obligatorisk</span>}
        </div>
        <div className="step-toggle-body">{body}</div>
      </div>
    </div>
  )
}
